import { motion } from 'framer-motion';
import React, { useState } from 'react';
import {
    FiBell,
    FiCheck,
    FiDatabase,
    FiEye,
    FiEyeOff,
    FiKey,
    FiMail,
    FiMonitor,
    FiMoon,
    FiSave,
    FiShield,
    FiSmartphone,
    FiSun,
    FiTarget,
    FiTrendingUp,
    FiUser,
    FiZap
} from 'react-icons/fi';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    // Profile settings
    name: 'Admin User',
    email: 'admin@innofusion.com',
    phone: '+1 (555) 123-4567',
    position: 'System Administrator',
    bio: 'Experienced system administrator with 5+ years in managing enterprise applications.',
    
    // Notification settings
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    weeklyReports: true,
    securityAlerts: true,
    
    // Security settings
    twoFactorAuth: true,
    sessionTimeout: 30,
    loginAlerts: true,
    
    // Appearance settings
    theme: 'dark',
    language: 'en',
    timezone: 'UTC-8',
    compactMode: false,
    
    // System settings
    autoBackup: true,
    backupFrequency: 'daily',
    logLevel: 'info',
    maintenanceMode: false
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FiUser, color: 'cyan' },
    { id: 'notifications', label: 'Notifications', icon: FiBell, color: 'emerald' },
    { id: 'security', label: 'Security', icon: FiShield, color: 'violet' },
    { id: 'appearance', label: 'Appearance', icon: FiMonitor, color: 'amber' },
    { id: 'system', label: 'System', icon: FiDatabase, color: 'pink' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        type: "spring",
        bounce: 0.3
      }
    }
  };

  const floatingAnimation = {
    y: [-8, 8, -8],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const renderProfileTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-6 font-display">
          Profile Information
        </h3>
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="flex items-center space-x-6 mb-8">
            <motion.div 
              className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-white font-bold text-2xl font-body">AU</span>
            </motion.div>
            <div>
              <h4 className="text-xl font-bold text-white font-heading">{settings.name}</h4>
              <p className="text-gray-400 font-body">{settings.position}</p>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="mt-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 px-4 py-2 rounded-xl text-sm border border-cyan-400/30 hover:from-cyan-500/30 hover:to-blue-500/30 transition-all duration-300 font-accent"
              >
                Change Avatar
              </motion.button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Full Name</label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => handleSettingChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 transition-all duration-300 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Email Address</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleSettingChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 transition-all duration-300 font-body"
                />
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Phone Number</label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleSettingChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 transition-all duration-300 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Position</label>
                <input
                  type="text"
                  value={settings.position}
                  onChange={(e) => handleSettingChange('position', e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 transition-all duration-300 font-body"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Bio</label>
            <textarea
              value={settings.bio}
              onChange={(e) => handleSettingChange('bio', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 transition-all duration-300 font-body resize-none"
              placeholder="Tell us about yourself..."
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderNotificationsTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent mb-6 font-display">
          Notification Preferences
        </h3>
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="space-y-6">
            {[
              { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive notifications via email', icon: FiMail },
              { key: 'pushNotifications', label: 'Push Notifications', desc: 'Browser push notifications', icon: FiBell },
              { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Text message alerts', icon: FiSmartphone },
              { key: 'weeklyReports', label: 'Weekly Reports', desc: 'Automated weekly summary reports', icon: FiTrendingUp },
              { key: 'securityAlerts', label: 'Security Alerts', desc: 'Important security notifications', icon: FiShield }
            ].map((item, index) => (
              <motion.div
                key={item.key}
                variants={itemVariants}
                className="flex items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.01 }}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center">
                    <item.icon className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white font-heading">{item.label}</h4>
                    <p className="text-gray-400 text-sm font-body">{item.desc}</p>
                  </div>
                </div>
                <motion.button
                  onClick={() => handleSettingChange(item.key, !settings[item.key])}
                  className={`relative w-14 h-8 rounded-full transition-all duration-300 ${
                    settings[item.key] 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-600' 
                      : 'bg-gray-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform duration-300 ${
                      settings[item.key] ? 'translate-x-6' : 'translate-x-0'
                    }`}
                    layout
                  />
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderSecurityTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent mb-6 font-display">
          Security Settings
        </h3>
        <div className="space-y-6">
          {/* Password Section */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Change Password</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Current Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-violet-500 transition-all duration-300 font-body pr-12"
                    placeholder="Enter current password"
                  />
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </motion.button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2 font-body">New Password</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-violet-500 transition-all duration-300 font-body"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Confirm Password</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-violet-500 transition-all duration-300 font-body"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Two Factor Auth */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="text-xl font-bold text-white font-heading">Two-Factor Authentication</h4>
                <p className="text-gray-400 font-body">Add an extra layer of security to your account</p>
              </div>
              <div className={`w-4 h-4 rounded-full ${settings.twoFactorAuth ? 'bg-emerald-500' : 'bg-gray-500'}`} />
            </div>
            <div className="flex items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/10">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FiKey className="text-white" size={20} />
                </div>
                <div>
                  <h5 className="font-bold text-white font-heading">Authenticator App</h5>
                  <p className="text-gray-400 text-sm font-body">Use Google Authenticator or similar app</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-xl font-accent transition-all duration-300 ${
                  settings.twoFactorAuth
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                    : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                }`}
              >
                {settings.twoFactorAuth ? 'Disable' : 'Enable'}
              </motion.button>
            </div>
          </div>

          {/* Session Settings */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Session Management</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Session Timeout (minutes)</label>
                <select
                  value={settings.sessionTimeout}
                  onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-violet-500 transition-all duration-300 font-body"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={480}>8 hours</option>
                </select>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <h5 className="font-bold text-white font-heading">Login Alerts</h5>
                  <p className="text-gray-400 text-sm font-body">Get notified of new login attempts</p>
                </div>
                <motion.button
                  onClick={() => handleSettingChange('loginAlerts', !settings.loginAlerts)}
                  className={`relative w-14 h-8 rounded-full transition-all duration-300 ${
                    settings.loginAlerts 
                      ? 'bg-gradient-to-r from-violet-500 to-purple-600' 
                      : 'bg-gray-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform duration-300 ${
                      settings.loginAlerts ? 'translate-x-6' : 'translate-x-0'
                    }`}
                    layout
                  />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderAppearanceTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent mb-6 font-display">
          Appearance & Display
        </h3>
        <div className="space-y-6">
          {/* Theme Selection */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Theme</h4>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { id: 'light', label: 'Light', icon: FiSun, preview: 'bg-gradient-to-br from-white to-gray-100' },
                { id: 'dark', label: 'Dark', icon: FiMoon, preview: 'bg-gradient-to-br from-gray-900 to-black' },
                { id: 'auto', label: 'Auto', icon: FiMonitor, preview: 'bg-gradient-to-br from-gray-500 to-gray-700' }
              ].map((theme) => (
                <motion.div
                  key={theme.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSettingChange('theme', theme.id)}
                  className={`p-6 rounded-2xl border cursor-pointer transition-all duration-300 ${
                    settings.theme === theme.id
                      ? 'border-amber-400/50 bg-amber-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className={`w-full h-24 ${theme.preview} rounded-xl mb-4 border border-white/20`} />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <theme.icon className="text-amber-400" size={20} />
                      <span className="font-bold text-white font-heading">{theme.label}</span>
                    </div>
                    {settings.theme === theme.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center"
                      >
                        <FiCheck className="text-black" size={12} />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Language & Region */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Language & Region</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Language</label>
                <select
                  value={settings.language}
                  onChange={(e) => handleSettingChange('language', e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-amber-500 transition-all duration-300 font-body"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Timezone</label>
                <select
                  value={settings.timezone}
                  onChange={(e) => handleSettingChange('timezone', e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-amber-500 transition-all duration-300 font-body"
                >
                  <option value="UTC-8">Pacific Time (UTC-8)</option>
                  <option value="UTC-5">Eastern Time (UTC-5)</option>
                  <option value="UTC+0">Greenwich Mean Time (UTC+0)</option>
                  <option value="UTC+1">Central European Time (UTC+1)</option>
                  <option value="UTC+8">China Standard Time (UTC+8)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Display Preferences */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Display Preferences</h4>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
              <div>
                <h5 className="font-bold text-white font-heading">Compact Mode</h5>
                <p className="text-gray-400 text-sm font-body">Reduce spacing for more content</p>
              </div>
              <motion.button
                onClick={() => handleSettingChange('compactMode', !settings.compactMode)}
                className={`relative w-14 h-8 rounded-full transition-all duration-300 ${
                  settings.compactMode 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600' 
                    : 'bg-gray-600'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform duration-300 ${
                    settings.compactMode ? 'translate-x-6' : 'translate-x-0'
                  }`}
                  layout
                />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderSystemTab = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={itemVariants}>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent mb-6 font-display">
          System Configuration
        </h3>
        <div className="space-y-6">
          {/* Backup Settings */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Backup Settings</h4>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <h5 className="font-bold text-white font-heading">Automatic Backup</h5>
                  <p className="text-gray-400 text-sm font-body">Enable scheduled backups</p>
                </div>
                <motion.button
                  onClick={() => handleSettingChange('autoBackup', !settings.autoBackup)}
                  className={`relative w-14 h-8 rounded-full transition-all duration-300 ${
                    settings.autoBackup 
                      ? 'bg-gradient-to-r from-pink-500 to-rose-600' 
                      : 'bg-gray-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform duration-300 ${
                      settings.autoBackup ? 'translate-x-6' : 'translate-x-0'
                    }`}
                    layout
                  />
                </motion.button>
              </div>
              
              {settings.autoBackup && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Backup Frequency</label>
                    <select
                      value={settings.backupFrequency}
                      onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-pink-500 transition-all duration-300 font-body"
                    >
                      <option value="hourly">Every Hour</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Log Settings */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Logging</h4>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2 font-body">Log Level</label>
              <select
                value={settings.logLevel}
                onChange={(e) => handleSettingChange('logLevel', e.target.value)}
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-pink-500 transition-all duration-300 font-body"
              >
                <option value="error">Error Only</option>
                <option value="warn">Warning & Above</option>
                <option value="info">Info & Above</option>
                <option value="debug">Debug (All)</option>
              </select>
            </div>
          </div>

          {/* Maintenance Mode */}
          <div className="bg-gradient-to-br from-red-600/20 via-orange-600/20 to-yellow-600/20 rounded-3xl p-8 border border-red-500/30 backdrop-blur-xl">
            <h4 className="text-xl font-bold text-white mb-6 font-heading">Maintenance Mode</h4>
            <div className="flex items-center justify-between p-6 bg-red-500/10 rounded-xl border border-red-500/20">
              <div>
                <h5 className="font-bold text-white font-heading flex items-center space-x-2">
                  <span>Enable Maintenance Mode</span>
                  {settings.maintenanceMode && <span className="text-red-400 animate-pulse">●</span>}
                </h5>
                <p className="text-gray-400 text-sm font-body">Temporarily disable access for maintenance</p>
              </div>
              <motion.button
                onClick={() => handleSettingChange('maintenanceMode', !settings.maintenanceMode)}
                className={`relative w-14 h-8 rounded-full transition-all duration-300 ${
                  settings.maintenanceMode 
                    ? 'bg-gradient-to-r from-red-500 to-orange-600' 
                    : 'bg-gray-600'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform duration-300 ${
                    settings.maintenanceMode ? 'translate-x-6' : 'translate-x-0'
                  }`}
                  layout
                />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black relative overflow-hidden">
      {/* Spectacular Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-violet-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-amber-500 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-blob animation-delay-4000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-1000"></div>
      </div>

      <div className="relative z-10 p-6 space-y-8 max-w-7xl mx-auto">
        {/* Spectacular Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="relative"
        >
          <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <motion.h1 
                  className="text-4xl font-bold bg-gradient-to-r from-pink-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent font-display mb-2"
                  animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
                  transition={{ duration: 5, repeat: Infinity }}
                >
                  Settings & Configuration 
                  <motion.span 
                    className="inline-block ml-3 text-3xl"
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  >
                    ⚙️
                  </motion.span>
                </motion.h1>
                <p className="text-xl text-gray-300 font-body">
                  Customize your <span className="text-pink-400 font-semibold">admin experience</span> and system preferences
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-pink-500 via-violet-500 to-cyan-600 text-white px-6 py-3 rounded-xl hover:from-pink-600 hover:to-cyan-700 transition-all duration-300 flex items-center space-x-2 font-accent shadow-lg"
              >
                <FiSave size={18} />
                <span>Save All Changes</span>
              </motion.button>
            </div>
            
            {/* Floating elements */}
            <motion.div
              className="absolute top-6 right-32 w-3 h-3 bg-pink-400 rounded-full"
              animate={floatingAnimation}
            />
            <motion.div
              className="absolute bottom-6 left-32 w-2 h-2 bg-violet-400 rounded-full"
              animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1 } }}
            />
          </div>
        </motion.div>

        {/* Enhanced Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl">
            <nav className="flex space-x-2 overflow-x-auto">
              {tabs.map((tab, index) => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-3 px-6 py-4 rounded-xl font-accent transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r from-${tab.color}-500/20 to-${tab.color}-600/20 text-${tab.color}-400 border border-${tab.color}-400/30`
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <tab.icon size={20} />
                  <span className="font-bold">{tab.label}</span>
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className={`w-2 h-2 bg-${tab.color}-400 rounded-full`}
                    />
                  )}
                </motion.button>
              ))}
            </nav>
          </div>
        </motion.div>

        {/* Enhanced Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'notifications' && renderNotificationsTab()}
          {activeTab === 'security' && renderSecurityTab()}
          {activeTab === 'appearance' && renderAppearanceTab()}
          {activeTab === 'system' && renderSystemTab()}
        </motion.div>

        {/* System Status Insights */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="bg-gradient-to-br from-pink-600/20 via-violet-600/20 to-cyan-600/20 rounded-3xl p-8 border border-pink-500/30 backdrop-blur-xl"
        >
          <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent mb-6 font-display">
            System Status & Performance
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: FiZap, title: 'System Health', desc: 'All systems operational. Server uptime: 99.9% this month with optimal performance across all modules.', color: 'pink' },
              { icon: FiTarget, title: 'Security Score', desc: 'Excellent security posture with all recommended security measures enabled and up-to-date.', color: 'violet' },
              { icon: FiTrendingUp, title: 'Performance', desc: 'Response times optimized. Average page load: 1.2s, 25% faster than industry average.', color: 'cyan' }
            ].map((insight, index) => (
              <motion.div 
                key={insight.title}
                className={`bg-gradient-to-br from-${insight.color}-500/20 to-${insight.color}-600/20 rounded-xl p-6 border border-${insight.color}-400/30 backdrop-blur-sm`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <insight.icon className={`text-${insight.color}-400`} size={24} />
                  <h4 className="font-semibold text-white font-heading">{insight.title}</h4>
                </div>
                <p className="text-sm text-gray-300 font-body leading-relaxed">
                  {insight.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Settings; 